import { FieldReportChip, WireCursor } from '@/ui/primitives'

import { ActivityCard } from './ActivityCard'
import { Markdown } from './Markdown'
import type { TranscriptEntry } from './transcript'

const MAX_INLINE = 10 * 1024

interface Props {
  turn: TranscriptEntry[]
  isStreaming: boolean
  showDetails: boolean
  onToggleDetails: () => void
  startedAtMs: number
  statusText?: string
}

function formatTime(ts?: string): string {
  if (!ts) return ''
  const value = new Date(ts)
  if (Number.isNaN(value.getTime())) return ''
  return value.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function currentTime(): string {
  return new Date().toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TurnView({
  turn,
  isStreaming,
  showDetails,
  onToggleDetails,
  startedAtMs,
  statusText,
}: Props) {
  const terminal = turn.find((e) => e.kind === 'done' || e.kind === 'error')
  const complete = !!terminal
  const finalAssistant = [...turn].reverse().find((e) => e.kind === 'assistant')
  const user = turn[0]?.kind === 'user' ? turn[0] : null
  const fallback = turn.find((e) => e.kind === 'provider_fallback')

  const detailEntries = turn.filter(
    (e) => e.kind === 'thinking' || e.kind === 'tool_use' || e.kind === 'tool_result',
  )
  const detailCount = detailEntries.length

  const fieldReports = pairFieldReports(detailEntries)
  const thinking = detailEntries.filter((e) => e.kind === 'thinking')
  const toolImages = detailEntries
    .filter((e) => e.kind === 'tool_result' && !e.isError)
    .flatMap((e) => (e.parts ?? []).filter((part) => part.type === 'image'))

  return (
    <article className="space-y-4 font-mono text-[14.5px]">
      {user && <UserLine entry={user} />}

      {fallback && <FallbackNotice entry={fallback} />}

      {isStreaming && fieldReports.length > 0 && (
        <div className="pl-3 border-l border-line space-y-1">
          {fieldReports.map((fr, i) => (
            <FieldReportChip
              key={i}
              header={fr.title}
              meta={fr.isError ? 'ERR' : fr.finishedAt ? formatTime(fr.finishedAt) : 'running…'}
              tone={fr.isError ? 'danger' : 'accent'}
              defaultOpen={fr.isError}
            >
              <CommandDetail command={fr.command} result={fr.result} />
            </FieldReportChip>
          ))}
        </div>
      )}

      {finalAssistant && (
        <AssistantBlock entry={finalAssistant} live={isStreaming && !complete} />
      )}

      {toolImages.length > 0 && <ToolImageStrip images={toolImages} />}

      {isStreaming && !complete && (
        <ActivityCard turn={turn} startedAt={startedAtMs} statusText={statusText} />
      )}

      {turn
        .filter((e) => e.kind === 'error')
        .map((e) => (
          <ErrorBlock key={e.id} entry={e} />
        ))}

      {terminal?.kind === 'done' && <DoneFooter entry={terminal} />}

      {complete && detailCount > 0 && (
        <div>
          <button
            type="button"
            onClick={onToggleDetails}
            className="text-fg-mute hover:text-fg transition-colors text-[13px]"
            aria-expanded={showDetails}
          >
            <span className="text-accent">{showDetails ? '▼' : '▶'}</span>{' '}
            {showDetails ? 'hide' : 'show'} details ({detailCount})
          </button>
          {showDetails && (
            <div className="mt-2 pl-3 border-l border-line space-y-2">
              {thinking.map((e) => (
                <ThinkingBlock key={e.id} entry={e} />
              ))}
              {fieldReports.map((fr, i) => (
                <FieldReportChip
                  key={`m-${i}`}
                  header={fr.title}
                  meta={fr.isError ? 'ERR' : undefined}
                  tone={fr.isError ? 'danger' : 'accent'}
                >
                  <CommandDetail command={fr.command} result={fr.result} />
                </FieldReportChip>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  )
}

function UserLine({ entry }: { entry: TranscriptEntry }) {
  return (
    <div className="animate-fade-in">
      <div className="text-fg-dim text-[12px] uppercase tracking-[0.08em] mb-1 flex items-center gap-2">
        <span>
          <span className="text-accent">$</span> you
        </span>
        <span aria-hidden>·</span>
        <span className="tnum">{formatTime(entry.ts)}</span>
      </div>
      <div className="user-bubble px-3 py-2 text-fg whitespace-pre-wrap break-words">
        {entry.text}
      </div>
    </div>
  )
}

function AssistantBlock({
  entry,
  live,
}: {
  entry: TranscriptEntry
  live: boolean
}) {
  const text = entry.text || ''
  const hasText = text.trim().length > 0
  return (
    <div className="animate-fade-in border-l border-line pl-3">
      <div className="text-fg-dim text-[12px] uppercase tracking-[0.08em] mb-1">
        <span className="text-accent">#</span> assistant ·{' '}
        {live ? currentTime() : formatTime(entry.ts)}
      </div>
      <div className="text-fg break-words leading-[1.6]">
        {hasText ? (
          <>
            <Markdown text={text} />
            {live && <WireCursor />}
          </>
        ) : (
          live && (
            <span className="text-fg-mute">
              thinking
              <WireCursor />
            </span>
          )
        )}
      </div>
    </div>
  )
}

function ThinkingBlock({ entry }: { entry: TranscriptEntry }) {
  return (
    <div className="text-[13px]">
      <div className="text-fg-dim uppercase tracking-[0.08em] text-[11px]">// thinking</div>
      <div className="mt-0.5 text-fg-mute italic whitespace-pre-wrap leading-relaxed">
        {entry.text}
      </div>
    </div>
  )
}

function ErrorBlock({ entry }: { entry: TranscriptEntry }) {
  return (
    <div className="border-l-2 border-danger pl-3 animate-fade-in" role="alert">
      <div className="text-danger uppercase tracking-[0.08em] text-[11.5px]">
        ✗ error
      </div>
      <div className="mt-0.5 text-fg text-[14.5px]">{entry.text}</div>
    </div>
  )
}

function FallbackNotice({ entry }: { entry: TranscriptEntry }) {
  return (
    <div className="border-l-2 border-warn pl-3 animate-fade-in">
      <div className="text-warn uppercase tracking-[0.08em] text-[11.5px]">
        ! model fallback
      </div>
      <div className="mt-0.5 text-fg text-[14px]">
        <span className="text-fg-mute">{entry.fromModel || '(unset)'}</span>{' '}
        <span className="text-fg-dim">→</span>{' '}
        <span className="text-warn">{entry.toModel}</span>
      </div>
      {entry.reason && (
        <div className="mt-0.5 text-[12px] text-fg-dim break-all">{entry.reason}</div>
      )}
    </div>
  )
}

function DoneFooter({ entry }: { entry: TranscriptEntry }) {
  return (
    <div className="pt-1 flex items-center gap-2 text-[11.5px] text-fg-dim tnum">
      <span className="text-accent">✓</span>
      <span>{entry.stopReason || 'done'}</span>
      <span>·</span>
      <span>${entry.cost?.toFixed(4) ?? '?'}</span>
    </div>
  )
}

function CommandDetail({ command, result }: { command: string; result: string | null }) {
  return (
    <div className="py-1">
      <div className="text-fg-dim uppercase text-[11px] tracking-[0.08em]">// cmd</div>
      <pre className="whitespace-pre-wrap break-all mt-0.5 text-fg">{command}</pre>
      {result !== null && (
        <>
          <div className="mt-2 text-fg-dim uppercase text-[11px] tracking-[0.08em]">
            // out
          </div>
          <pre className="whitespace-pre-wrap break-all mt-0.5 text-fg-soft">
            {result.length > MAX_INLINE
              ? result.slice(0, MAX_INLINE) + '\n…(truncated; full output saved server-side)'
              : result}
          </pre>
        </>
      )}
    </div>
  )
}

function ToolImageStrip({
  images,
}: {
  images: Array<{ type: 'image'; mediaType: string; data: string }>
}) {
  return (
    <div className="space-y-2 border-l border-line pl-3 animate-fade-in">
      <div className="text-fg-dim text-[12px] uppercase tracking-[0.08em]">
        <span className="text-accent">#</span> screenshot
      </div>
      <div className="space-y-3">
        {images.map((image, idx) => (
          <figure
            key={`${image.mediaType}-${idx}`}
            className="panel-inset overflow-hidden border border-line"
          >
            <img
              src={`data:${image.mediaType};base64,${image.data}`}
              alt={`Tool screenshot ${idx + 1}`}
              className="block w-full h-auto"
              loading="lazy"
            />
          </figure>
        ))}
      </div>
    </div>
  )
}

interface PairedFieldReport {
  title: string
  command: string
  result: string | null
  isError: boolean
  finishedAt?: string
}

function formatToolLabel(toolName?: string, description?: string): string {
  if (description) return description
  if (!toolName) return 'tool'
  const match = toolName.match(/^mcp__([^_]+(?:-[^_]+)*)__([^]+)$/)
  return match ? `${match[1]}:${match[2]}` : toolName
}

function pairFieldReports(details: TranscriptEntry[]): PairedFieldReport[] {
  const out: PairedFieldReport[] = []
  let pending: PairedFieldReport | null = null
  for (const e of details) {
    if (e.kind === 'tool_use') {
      if (pending) out.push(pending)
      const label = formatToolLabel(e.toolName, e.description)
      pending = {
        title: label,
        command: e.command || '(no tool input)',
        result: null,
        isError: false,
      }
    } else if (e.kind === 'tool_result') {
      if (pending) {
        pending.result = e.text || ''
        pending.isError = !!e.isError
        pending.finishedAt = e.ts
        out.push(pending)
        pending = null
      } else {
        out.push({
          title: 'response',
          command: '',
          result: e.text || '',
          isError: !!e.isError,
          finishedAt: e.ts,
        })
      }
    }
  }
  if (pending) out.push(pending)
  return out
}

export function EntryView() {
  return null
}
